import React, { useEffect, useState, useContext, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import Container from 'components/Container'
import Row from 'components/Row'
import Col from 'components/Col'
import Table from 'components/Table'
import { SiteContext } from 'providers/site'
import { makeCalculationRowEco, makeCalculationsColumnsEco, mapEcoCalculatorDataFromApi } from './ecosystemTable'
import { makeCalculationsRowsOtherCriteria, makeCalculationsColumnsOtherCriteria } from './otherCriteriaTable'
import { FullWidthCalculationSection, StyledCalculationTableEco } from './style'
import { requestCalculationData, requestEcoSystemCalculator, requestTreatmentData } from 'api/site'
import AssessmentTitle from 'components/AssessmentTitle'
import {
  makeCalculationsColumnsCongregationCriteria,
  makeCalculationsRowsCongregationCriteria
} from './congregationTable'
import { SITE_STATUS_DRAFT_ID, SITE_STATUS_NOMINATED_ID, SITE_STATUS_PROPOSED_ID, SITE_STATUS_REVIEWED_BY_RFP_ID } from 'constants/siteStatuses'
import { canViewSensitive } from 'utils/rolePermissions'
import { ProfileContext } from 'providers/profile'
import { getCalculationCongregations } from 'api/congregations'
import { showErrorToast } from 'utils/toast'
import { useIBAAccess } from 'hooks/useIBAAccess'
import CalculationsSpeciesTable from './CalculationsSpeciesTable'
import { mapCalculationDataFromApi } from './mapCalculationDataFromApi'
import { resolveSiteWorkflowContext } from 'utils/resolveSiteWorkflowIds'
import CalculationsErrorBoundary from './CalculationsErrorBoundary'
import { reportCalculationsError, runCalculationsSafely } from './calculationsErrorHandling'
import KbaCriteriaSummary from './components/KbaCriteriaSummary'
import { computeOverallKbaCriteria, resolveCatalogCriteriaFallback } from './utils/computeOverallKbaCriteria'

const ALL_SPECIES_RESULTS_PER_PAGE = 10000

const Calculations = () => {
  const params = useParams()
  const [calculationData, setCalculationData] = useState([])
  const [isSpeciesLoading, setIsSpeciesLoading] = useState(true)
  const [isEcoLoading, setIsEcoLoading] = useState(true)
  const [isTreatmentLoading, setIsTreatmentLoading] = useState(true)
  const [calculationDataEco, setCalculationDataEco] = useState([])
  const [congregationData, setCongregationData] = useState([])
  const [treatmentData, setTreatmentData] = useState({})
  const { site, isEditable } = useContext(SiteContext)
  const { profile } = useContext(ProfileContext)
  const {
    canSeeSiteIBACongregations,
    canSeeIBACriteria,
    canSeeSiteIBACriteria,
    shouldShowSiteIBAData
  } = useIBAAccess()

  const { siteId, historyId, editOnly } = useMemo(() => {
    return resolveSiteWorkflowContext(site, params)
  }, [site, params])

  const isActiveReassessment = Boolean(params.siteHistoryId) && !editOnly

  const disabled = !isEditable || site.SiteStatusId !== SITE_STATUS_DRAFT_ID

  const showCongregations = canSeeSiteIBACongregations(site) || (site.SiteStatusId === SITE_STATUS_PROPOSED_ID || site.SiteStatusId === SITE_STATUS_NOMINATED_ID || site.SiteStatusId === SITE_STATUS_REVIEWED_BY_RFP_ID || canViewSensitive(profile.RoleId))

  const getCongregationsData = async () => {
    try {
      const response = await getCalculationCongregations(siteId, historyId)

      if (response?.error) {
        showErrorToast(response.error)
        setCongregationData([])
        return
      }

      setCongregationData(Array.isArray(response) ? response : [])
    } catch (error) {
      reportCalculationsError('fetchCongregations', error, { siteId, historyId })
      showErrorToast(error)
      setCongregationData([])
    }
  }

  const getCalculationData = () => {
    setIsSpeciesLoading(true)

    requestCalculationData(siteId, historyId, {
      resultsPerPage: ALL_SPECIES_RESULTS_PER_PAGE,
      currentPage: 1
    }).then((data) => {
      if (!Array.isArray(data?.parsed)) {
        setCalculationData([])
        return
      }

      try {
        setCalculationData(mapCalculationDataFromApi(data.parsed))
      } catch (error) {
        reportCalculationsError('mapCalculationDataFromApi', error)
        showErrorToast(error)
        setCalculationData([])
      }
    }).catch((error) => {
      reportCalculationsError('fetchCalculationData', error, { siteId, historyId })
      showErrorToast(error)
      setCalculationData([])
    }).finally(() => {
      setIsSpeciesLoading(false)
    })
  }

  const getCalculationDataEco = () => {
    setIsEcoLoading(true)

    requestEcoSystemCalculator(siteId, historyId).then((data) => {
      setCalculationDataEco(mapEcoCalculatorDataFromApi(data))
    }).catch((error) => {
      reportCalculationsError('fetchEcoCalculatorData', error, { siteId, historyId })
      showErrorToast(error)
      setCalculationDataEco([])
    }).finally(() => {
      setIsEcoLoading(false)
    })
  }

  const getTreatmentData = () => {
    setIsTreatmentLoading(true)

    requestTreatmentData(siteId, historyId).then((data) => {
      setTreatmentData(data && typeof data === 'object' ? data : {})
    }).catch((error) => {
      reportCalculationsError('fetchTreatmentData', error, { siteId, historyId })
      showErrorToast(error)
      setTreatmentData({})
    }).finally(() => {
      setIsTreatmentLoading(false)
    })
  }

  useEffect(() => {
    if (!siteId) {
      return
    }

    getCalculationData()
    getCalculationDataEco()
    getTreatmentData()
    getCongregationsData()
  }, [siteId, historyId])

  const rowsEco = useMemo(() => {
    return runCalculationsSafely(
      'buildEcoRows',
      () => makeCalculationRowEco(calculationDataEco, site),
      []
    )
  }, [calculationDataEco, site])

  const columnsEco = makeCalculationsColumnsEco()

  const rowsOtherCriteria = useMemo(() => {
    return runCalculationsSafely(
      'buildOtherCriteriaRows',
      () => makeCalculationsRowsOtherCriteria(treatmentData),
      []
    )
  }, [treatmentData])

  const columnsOtherCriteria = makeCalculationsColumnsOtherCriteria()

  const rowsCongregationCriteria = useMemo(() => {
    return runCalculationsSafely(
      'buildCongregationRows',
      () => makeCalculationsRowsCongregationCriteria(congregationData),
      []
    )
  }, [congregationData])

  const columnsCongregationCriteria = makeCalculationsColumnsCongregationCriteria(canSeeIBACriteria)

  const overallCriteria = useMemo(() => {
    const computed = runCalculationsSafely(
      'buildOverallKbaCriteria',
      () => computeOverallKbaCriteria({
        species: calculationData,
        eco: calculationDataEco,
        treatment: treatmentData,
        isReassessment: isActiveReassessment
      }),
      []
    )

    if (computed.length > 0) {
      return computed
    }

    if (!isActiveReassessment) {
      return resolveCatalogCriteriaFallback(site)
    }

    return computed
  }, [calculationData, calculationDataEco, treatmentData, isActiveReassessment, site])

  const isOverallCriteriaLoading = isSpeciesLoading || isEcoLoading || isTreatmentLoading

  return (
    <CalculationsErrorBoundary label="calculations page">
      <KbaCriteriaSummary criteria={overallCriteria} isLoading={isOverallCriteriaLoading} />
      <Container>
        <FullWidthCalculationSection>
          <AssessmentTitle
            titleId='speciesDetails'
            subTitleId='calculationsSubTittle'
            marginTop='32px'
          />
          <CalculationsErrorBoundary label="species table">
            <CalculationsSpeciesTable
              calculationData={calculationData}
              isLoading={isSpeciesLoading}
              siteId={siteId}
              historyId={historyId}
              site={site}
              disabled={disabled}
              showUnderAssessmentFilter={Boolean(site.reassessmentFlag)}
              canSeeSiteIBACriteria={canSeeSiteIBACriteria}
              shouldShowSiteIBAData={shouldShowSiteIBAData}
            />
          </CalculationsErrorBoundary>
        </FullWidthCalculationSection>
        <FullWidthCalculationSection>
          <AssessmentTitle
            titleId='ecosystemDataTable'
            subTitleId='ecosystemDataTableSubTitle'
            marginTop='64px'
          />
          <Row>
            <Col md={12}>
              <StyledCalculationTableEco>
                <Table
                  rows={rowsEco}
                  columns={columnsEco}
                  dataTest="calculations-table"
                  noResultMessage="noEcosystemsSelected"
                  noClearFilter={true}
                />
              </StyledCalculationTableEco>
            </Col>
          </Row>
          <AssessmentTitle
            titleId='otherCriteriaDataTable'
            subTitleId='otherCriteriaDataTableDescription'
            marginTop='64px'
          />
          <Row>
            <Col md={12}>
              <StyledCalculationTableEco>
                <Table
                  rows={rowsOtherCriteria}
                  columns={columnsOtherCriteria}
                  dataTest="calculations-table"
                  noResultMessage="noEcosystemsSelected"
                  noClearFilter={true}
                />
              </StyledCalculationTableEco>
            </Col>
          </Row>
        </FullWidthCalculationSection>
        {showCongregations && (
          <>
            <AssessmentTitle
              titleId='congregationTable'
              subTitleId='congregationDataTableSubTitle'
              marginTop='64px'
            />
            <Row>
              <Col md={12}>
                <StyledCalculationTableEco>
                  <Table
                    rows={rowsCongregationCriteria}
                    columns={columnsCongregationCriteria}
                    dataTest="congregation-table"
                    noResultMessage="noCongregationSelected"
                    noClearFilter={true}
                  />
                </StyledCalculationTableEco>
              </Col>
            </Row>
          </>
        )}
      </Container>
    </CalculationsErrorBoundary>
  )
}

export default Calculations
